import type { ReactNode } from "react";
import Box from "@ui-kit/layout/Box/Box.web";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import { CheckoutTrackingProvider } from "@app-contexts/public/shared/checkout/contexts/useCheckoutTrackingContext";
import ReviewItemGroup from "@app-contexts/public/shared/checkout/components/steps/review/components/ReviewItemGroup/ReviewItemGroup.web";
import CartItem from "@app-contexts/public/features/basket/components/cart-summary/cart-summary-items/cart-item/CartItem/CartItem.web";
import { useCartSummaryData } from "@app-contexts/public/features/basket/hooks/useCartSummaryData";
import type { GetBasketHoldParamType } from "@app-contexts/user/features/basket/data/types/basket";
import CartRecommendations from "./components/CartRecommendations/CartRecommendations.web";
import CartSummaryFooter from "./components/CartSummaryFooter/CartSummaryFooter.web";

export type EnhancedCartSummaryContentProps = {
  onCheckout: () => void;
  showProducts?: boolean;
  showEvents?: boolean;
  dynamicHoldParams?: GetBasketHoldParamType;
  compactItemGroups?: boolean;
  inset?: boolean;
  showCheckoutActions?: boolean;
  renderBeforeItems?: ReactNode;
  renderAfterFooter?: ReactNode;
};

export default function EnhancedCartSummaryContent({
  onCheckout,
  showProducts,
  showEvents,
  dynamicHoldParams,
  compactItemGroups = false,
  inset = false,
  showCheckoutActions = true,
  renderBeforeItems,
  renderAfterFooter,
}: EnhancedCartSummaryContentProps): JSX.Element {
  const {
    hasItems,
    displayItemGroups,
    holdDetailsData,
    basketUpgradesData,
    isBasketUpdating,
    basketItemData,
  } = useCartSummaryData({
    enableItemData: compactItemGroups,
    dynamicHoldParams,
  });

  return (
    <CheckoutTrackingProvider>
      <Flex flexDirection="column" height="full" overflow="hidden">
        {renderBeforeItems}
        {hasItems && (
          <Box
            flex="1"
            overflowY="auto"
            minHeight={0}
            overflowX="hidden"
            px={inset ? "md" : undefined}
          >
            <Flex
              direction="column"
              gap="md"
              borderTopWidth="1px"
              borderColor="border"
              pt="lg"
            >
              {displayItemGroups.map((item) => {
                // Dynamic hold baskets enforce a strict item-group whitelist on the backend
                // (DynamicHoldValidator._set_hold_item_groups_and_limits). Only ticket types,
                // products, and memberships explicitly present in the hold's own inventory are
                // allowed — anything else raises TVEM_UNFULFILLABLE_LINE_ITEM. Upgrade offers
                // would add items outside that whitelist, so we suppress them here.
                // Regular (non-dynamic) holds have no such whitelist restriction.
                const upgradeInfo =
                  !dynamicHoldParams && item.id != null
                    ? basketUpgradesData?.[item.id]
                    : undefined;

                return compactItemGroups ? (
                  <CartItem
                    key={item.id}
                    item={item}
                    disabled={isBasketUpdating}
                    venue={basketItemData?.eventsMapping[item.event]?.venue}
                    event={basketItemData?.eventsMapping[item.event]}
                    product={basketItemData?.productsMapping[item.product]}
                    ticketType={
                      basketItemData?.ticketTypesMapping[
                        `${item.event}:${item.type}`
                      ]
                    }
                    productAttribute={
                      basketItemData?.productsAttributesMapping[
                        `${item.product}:${item.product_attribute}`
                      ]
                    }
                    membershipLevel={
                      basketItemData?.membershipLevelsMapping[
                        `${item.membership_group}:${item.membership_level}`
                      ]
                    }
                    holdPurchaseLimits={
                      holdDetailsData?.on_hold_detail?.purchase_limit_min !=
                        null &&
                      holdDetailsData?.on_hold_detail?.purchase_limit_max !=
                        null
                        ? {
                            min: holdDetailsData.on_hold_detail
                              .purchase_limit_min,
                            max: holdDetailsData.on_hold_detail
                              .purchase_limit_max,
                          }
                        : undefined
                    }
                    itemGroupUpgrades={upgradeInfo}
                  />
                ) : (
                  <ReviewItemGroup
                    key={item.id}
                    itemGroup={item}
                    allowQuantityChange
                    dynamicHoldParams={dynamicHoldParams}
                  />
                );
              })}
            </Flex>
            {!compactItemGroups && (
              <CartRecommendations
                showProducts={showProducts}
                showEvents={showEvents}
              />
            )}
          </Box>
        )}
        <Box
          flexShrink={0}
          pt={hasItems ? "md" : "none"}
          px={inset ? "md" : undefined}
          pb={inset ? "md" : undefined}
          borderTopWidth={hasItems ? "1px" : "0"}
          borderColor="border"
        >
          <CartSummaryFooter
            onCheckout={onCheckout}
            showCheckoutActions={showCheckoutActions}
          />
          {renderAfterFooter}
        </Box>
      </Flex>
    </CheckoutTrackingProvider>
  );
}
